import { useCallback } from "react";
import { toNumber } from "../utils/numberUtils";
import { getOrCreatePeriod } from "../api/periods";

export default function useProductionWorkflow({
  companyApi,
  productionReportForm,
  productionMetricEntries,
  productionMetrics,
  setProductionReportSaveState,
  setAddProductionReportOpen,
  setProductionReportDialogMode,
  onProductionReportSaved,
}) {
  const prepareProductionMetricEntries = useCallback(() => {
    if (!productionReportForm.resource_reserve_id) {
      return { error: "Resource reserve is required." };
    }
    if (!productionReportForm.as_of_date) {
      return { error: "Period ending date is required." };
    }
    if (!["year", "quarter"].includes(productionReportForm.period_type)) {
      return { error: "Production reports must be annual or quarterly." };
    }
    if (productionReportForm.period_type === "year" && !productionReportForm.fiscal_year) {
      return { error: "Fiscal year is required for annual reports." };
    }
    if (
      productionReportForm.period_type === "quarter" &&
      (!productionReportForm.fiscal_year || !productionReportForm.fiscal_quarter)
    ) {
      return { error: "Fiscal year and quarter are required for quarterly reports." };
    }
    const metricRowsWithValues = productionMetricEntries.filter(
      (entry) => !entry.isAddRow && entry.metric_id && `${entry.value}`.trim()
    );
    const nonNumericRow = metricRowsWithValues.find(
      (entry) => toNumber(entry.value) === null
    );
    if (nonNumericRow) {
      return { error: "Production metric values must be numeric." };
    }
    const invalidMetricRow = metricRowsWithValues.find((entry) => {
      const metric = productionMetrics.find((m) => m.id === entry.metric_id);
      if (!metric) return true;
      if (metric.commodity_required && !entry.commodity_id) return true;
      if (!metric.commodity_required && entry.commodity_id) return true;
      return false;
    });
    if (invalidMetricRow) {
      return { error: "Check commodity requirements for the metrics you entered." };
    }
    const seen = new Set();
    for (const entry of metricRowsWithValues) {
      const key = `${entry.metric_id}-${entry.commodity_id || ""}`;
      if (seen.has(key)) {
        return { error: "Duplicate metric rows are not allowed." };
      }
      seen.add(key);
    }
    return { metricRowsWithValues };
  }, [productionReportForm, productionMetricEntries, productionMetrics]);

  const submitProductionReport = useCallback(async () => {
    const { metricRowsWithValues, error } = prepareProductionMetricEntries();
    if (error) {
      setProductionReportSaveState({ loading: false, error, success: null });
      return;
    }
    setProductionReportSaveState({ loading: true, error: null, success: null });
    try {
      const label =
        productionReportForm.period_type === "quarter"
          ? `FY${productionReportForm.fiscal_year} Q${productionReportForm.fiscal_quarter}`
          : productionReportForm.period_type === "year"
          ? `FY${productionReportForm.fiscal_year}`
          : productionReportForm.as_of_date;
      const period = await getOrCreatePeriod({
        period_type: productionReportForm.period_type,
        as_of_date: productionReportForm.as_of_date,
        fiscal_year: toNumber(productionReportForm.fiscal_year),
        fiscal_quarter: toNumber(productionReportForm.fiscal_quarter),
        label,
      });
      const reportPayload = {
        resource_reserve: productionReportForm.resource_reserve_id,
        period: period.id,
        source_title: productionReportForm.source_title || "",
        source_url: productionReportForm.source_url || "",
        source_notes: productionReportForm.source_notes || "",
      };
      const valueRows = metricRowsWithValues.map((entry) => ({
        metric: entry.metric_id,
        commodity: entry.commodity_id || null,
        value: toNumber(entry.value),
      }));
      const report = await companyApi.saveProductionReportWithValues(
        reportPayload,
        valueRows
      );
      const values = report?.values || valueRows;
      setProductionReportSaveState({
        loading: false,
        error: null,
        success: "Production report saved as draft.",
      });
      setAddProductionReportOpen(false);
      if (onProductionReportSaved) {
        onProductionReportSaved(productionReportForm.resource_reserve_id, report, values);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.response?.data?.period || "Unable to save report.";
      setProductionReportSaveState({ loading: false, error: detail, success: null });
    }
  }, [
    companyApi,
    onProductionReportSaved,
    prepareProductionMetricEntries,
    productionReportForm,
    setAddProductionReportOpen,
    setProductionReportSaveState,
  ]);

  const updateProductionReport = useCallback(async () => {
    if (!productionReportForm.report_id) return;
    const { metricRowsWithValues, error } = prepareProductionMetricEntries();
    if (error) {
      setProductionReportSaveState({ loading: false, error, success: null });
      return;
    }
    setProductionReportSaveState({ loading: true, error: null, success: null });
    try {
      const label =
        productionReportForm.period_type === "quarter"
          ? `FY${productionReportForm.fiscal_year} Q${productionReportForm.fiscal_quarter}`
          : productionReportForm.period_type === "year"
          ? `FY${productionReportForm.fiscal_year}`
          : productionReportForm.as_of_date;
      const period = await getOrCreatePeriod({
        period_type: productionReportForm.period_type,
        as_of_date: productionReportForm.as_of_date,
        fiscal_year: toNumber(productionReportForm.fiscal_year),
        fiscal_quarter: toNumber(productionReportForm.fiscal_quarter),
        label,
      });
      const reportPayload = {
        resource_reserve: productionReportForm.resource_reserve_id,
        period: period.id,
        source_title: productionReportForm.source_title || "",
        source_url: productionReportForm.source_url || "",
        source_notes: productionReportForm.source_notes || "",
      };
      const valueRows = metricRowsWithValues.map((entry) => ({
        metric: entry.metric_id,
        commodity: entry.commodity_id || null,
        value: toNumber(entry.value),
      }));
      const updatedReport = await companyApi.saveProductionReportWithValues(
        reportPayload,
        valueRows,
        productionReportForm.report_id
      );
      const values = updatedReport?.values || valueRows;
      setProductionReportSaveState({
        loading: false,
        error: null,
        success: "Production report updated.",
      });
      setAddProductionReportOpen(false);
      setProductionReportDialogMode("create");
      if (onProductionReportSaved) {
        onProductionReportSaved(productionReportForm.resource_reserve_id, updatedReport, values);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.response?.data?.period || "Unable to update report.";
      setProductionReportSaveState({ loading: false, error: detail, success: null });
    }
  }, [
    companyApi,
    onProductionReportSaved,
    prepareProductionMetricEntries,
    productionReportForm,
    setAddProductionReportOpen,
    setProductionReportDialogMode,
    setProductionReportSaveState,
  ]);

  return {
    submitProductionReport,
    updateProductionReport,
  };
}
